import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Search, Download, FileDown, Upload, Plus,
    Edit2, Trash2, Save, FileSpreadsheet, X, Layers, ClipboardList
} from "lucide-react";
import {
    fetchSkillsRequest,
    createSkillRequest,
    updateSkillRequest,
    deleteSkillRequest,
    downloadSkillsTemplateRequest,
    importSkillsExcelRequest,
    exportSkillsRequest,
    clearSkillsImportResult,
} from "../../../redux/actions/skillsActions";
import {
    fetchModesEvaluationRequest,
    createModeEvaluationRequest,
    updateModeEvaluationRequest,
    deleteModeEvaluationRequest,
} from "../../../redux/actions/modeEvaluationActions";

import {
    fetchDomainesRequest,
    fetchThemesRequest,
    fetchThemesByDomaineRequest,
} from "../../../redux/actions/paramDomainesThemesActions";

import {
    fetchNiveauSkillsRequest,
    createNiveauSkillRequest,
    updateNiveauSkillRequest,
    deleteNiveauSkillRequest,
} from "../../../redux/actions/niveauSkillActions";

import "../admin/SkillManagement.css";

const SKILL_TYPES = ["METIER", "SOFT_SKILLS"];

const emptySkillForm = {
    code:        "",
    nom:         "",
    description: "",
    type:        "METIER",
    domaineId:   "",
    themeId:     "",
};

const emptyNiveauForm = {
    nom:         "",
    description: "",
    niveau:      "",
};

/* ── Modal réutilisable ── */
const Modal = ({ open, title, onClose, size = "lg", footer, children }) => {
    if (!open) return null;
    const sizeClass =
        size === "xl" ? "crModal__panel--xl" :
            size === "lg" ? "crModal__panel--lg" : "";
    return (
        <div className="crModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className={`crModal__panel ${sizeClass}`} onMouseDown={(e) => e.stopPropagation()}>
                <div className="crModal__header">
                    <div className="crModal__title">{title}</div>
                    <button className="crIconBtn" type="button" onClick={onClose} aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="crModal__body">{children}</div>
                {footer && <div className="crModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

const emptyModeForm = {
    nom:         "",
    code:        "",
    description: "",
};

/* ══════════════════════════════════════════════
   ONGLET — GESTION DES MODES D'ÉVALUATION
══════════════════════════════════════════════ */
const ModesEvaluationTab = () => {
    const dispatch = useDispatch();

    const { modesEvaluation, loading } = useSelector((state) => state.modesEvaluation || {});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing,     setEditing]     = useState(null);
    const [form,        setForm]        = useState(emptyModeForm);
    const [q,           setQ]           = useState("");

    useEffect(() => { dispatch(fetchModesEvaluationRequest()); }, [dispatch]);

    const modes    = useMemo(() => (Array.isArray(modesEvaluation) ? modesEvaluation : []), [modesEvaluation]);
    const filtered = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        return modes.filter((m) =>
            !t ||
            (m.nom         || "").toLowerCase().includes(t) ||
            (m.code        || "").toLowerCase().includes(t) ||
            (m.description || "").toLowerCase().includes(t)
        );
    }, [modes, q]);

    const openCreate = () => { setEditing(null); setForm(emptyModeForm); setIsModalOpen(true); };
    const openEdit   = (m) => { setEditing(m); setForm({ nom: m.nom || "", code: m.code || "", description: m.description || "" }); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); setEditing(null); setForm(emptyModeForm); };

    const onChange = (e) => { const { name, value } = e.target; setForm((p) => ({ ...p, [name]: value })); };

    const submit = (e) => {
        e.preventDefault();
        if (!form.nom?.trim())  { Swal.fire({ icon: "warning", text: "Nom obligatoire." });  return; }
        if (!form.code?.trim()) { Swal.fire({ icon: "warning", text: "Code obligatoire." }); return; }
        const payload = { nom: form.nom.trim(), code: form.code.trim().toUpperCase(), description: form.description || "" };
        if (editing?.id) dispatch(updateModeEvaluationRequest(editing.id, payload));
        else             dispatch(createModeEvaluationRequest(payload));
        closeModal();
    };

    const confirmDelete = (m) => {
        Swal.fire({ title: "Supprimer", text: `Supprimer le mode "${m.nom}" ?`, icon: "warning", showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non", confirmButtonColor: "#d33" })
            .then((res) => { if (res.isConfirmed) dispatch(deleteModeEvaluationRequest(m.id)); });
    };

    return (
        <>
            {/* ── En-tête ── */}
            <div className="crHeader">
                <div>
                    <h1>Modes d'évaluation</h1>
                    <p>Définissez les modes d'évaluation des compétences</p>
                </div>
                <div className="crHeaderActions">
                    <button className="crBtn crBtn--primary" onClick={openCreate} type="button">
                        <Plus size={16} /> Ajouter un mode
                    </button>
                </div>
            </div>

            {/* ── Filtres ── */}
            <div className="crToolbar">
                <input type="text" className="crSearch" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, code, description..." />
                <div className="crMeta">{loading ? "..." : `${filtered.length} mode${filtered.length !== 1 ? "s" : ""}`}</div>
            </div>

            {/* ── Tableau ── */}
            <div className="crWrap">
                <div className="crCard">
                    <div className="crBody">
                        <div className="crTopRow">
                            <h2 className="crTitle">Modes ({loading ? "..." : filtered.length})</h2>
                        </div>
                        <div className="crTableWrap">
                            <table className="crTable">
                                <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Nom</th>
                                    <th>Description</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.length === 0 ? (
                                    <tr><td colSpan={4} className="crEmptyCell">Aucun mode d'évaluation défini</td></tr>
                                ) : (
                                    filtered.map((m) => (
                                        <tr key={m.id}>
                                            <td><span className="crStrong">{m.code}</span></td>
                                            <td>{m.nom}</td>
                                            <td className="crMuted">
                                                {m.description
                                                    ? m.description.length > 80 ? m.description.slice(0, 80) + "…" : m.description
                                                    : "—"}
                                            </td>
                                            <td className="is-right">
                                                <div className="crRowActions">
                                                    <button className="crIconBtn" title="Modifier" onClick={() => openEdit(m)} type="button"><Edit2 size={16} /></button>
                                                    <button className="crIconBtn crIconBtn--danger" title="Supprimer" onClick={() => confirmDelete(m)} type="button"><Trash2 size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ══ Modal CRUD ══ */}
            <Modal open={isModalOpen} title={editing ? "Modifier mode" : "Ajouter un mode"} onClose={closeModal} size="lg"
                   footer={
                       <>
                           <button className="crBtn crBtn--ghost" onClick={closeModal} disabled={loading} type="button">Annuler</button>
                           <button className="crBtn crBtn--primary" onClick={submit} disabled={loading} type="button">
                               <Save size={16} />{loading ? "En cours..." : editing ? "Enregistrer" : "Créer"}
                           </button>
                       </>
                   }
            >
                <form className="crFormContent" onSubmit={submit}>
                    <div className="crFormSection">
                        <h3 className="crFormTitle">Identification</h3>
                        <div className="crFormGrid">
                            <div className="crField">
                                <label>Code *</label>
                                <input name="code" value={form.code} onChange={onChange} placeholder="ex: AUTO_EVAL" />
                            </div>
                            <div className="crField">
                                <label>Nom *</label>
                                <input name="nom" value={form.nom} onChange={onChange} placeholder="ex: Auto-évaluation" />
                            </div>
                            <div className="crField crField--full">
                                <label>Description</label>
                                <textarea name="description" value={form.description} onChange={onChange} rows={4} placeholder="Décrivez ce mode d'évaluation..." />
                            </div>
                        </div>
                    </div>
                    <button type="submit" style={{ display: "none" }} />
                </form>
            </Modal>
        </>
    );
};

/* ══════════════════════════════════════════════
   ONGLET — GESTION DES COMPÉTENCES
══════════════════════════════════════════════ */
const SkillsTab = () => {
    const dispatch = useDispatch();

    const { list, loading, bulkLoading, error, importResult } =
        useSelector((state) => state.skills || {});

    const { domaines, themesFiltered, themes } =
        useSelector((state) => state.paramDomainesThemes || {});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing,     setEditing]     = useState(null);
    const [form,        setForm]        = useState(emptySkillForm);

    const [bulkOpen,   setBulkOpen]   = useState(false);
    const [bulkFile,   setBulkFile]   = useState(null);
    const [bulkDryRun, setBulkDryRun] = useState(true);
    const [bulkAction, setBulkAction] = useState("add");

    const [q,          setQ]          = useState("");
    const [filterType, setFilterType] = useState("");

    useEffect(() => {
        dispatch(fetchSkillsRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (error) {
            Swal.fire({ icon: "error", title: "Erreur", text: typeof error === "string" ? error : "Une erreur est survenue" });
        }
    }, [error]);

    const skills   = useMemo(() => (Array.isArray(list) ? list : []), [list]);
    const filtered = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        return skills.filter((s) => {
            const matchQ =
                !t ||
                (s.code        || "").toLowerCase().includes(t) ||
                (s.nom         || "").toLowerCase().includes(t) ||
                (s.description || "").toLowerCase().includes(t) ||
                (s.domaineNom  || "").toLowerCase().includes(t) ||
                (s.themeNom    || "").toLowerCase().includes(t);
            const matchType = !filterType || s.type === filterType;
            return matchQ && matchType;
        });
    }, [skills, q, filterType]);

    const themesToUse = useMemo(() => {
        if (Array.isArray(themesFiltered) && themesFiltered.length) return themesFiltered;
        return Array.isArray(themes) ? themes : [];
    }, [themesFiltered, themes]);

    const openCreate = () => { setEditing(null); setForm(emptySkillForm); setIsModalOpen(true); };
    const openEdit   = (s) => {
        setEditing(s);
        setForm({ code: s.code || "", nom: s.nom || "", description: s.description || "", type: s.type || "METIER", domaineId: s.domaineId || "", themeId: s.themeId || "" });
        if (s.domaineId) dispatch(fetchThemesByDomaineRequest(s.domaineId));
        setIsModalOpen(true);
    };
    const closeModal = () => { setIsModalOpen(false); setEditing(null); setForm(emptySkillForm); };

    const onChange        = (e) => { const { name, value } = e.target; setForm((p) => ({ ...p, [name]: value })); };
    const onSelectDomaine = (e) => {
        const domaineId = e.target.value;
        setForm((p) => ({ ...p, domaineId, themeId: "" }));
        if (domaineId) dispatch(fetchThemesByDomaineRequest(Number(domaineId)));
    };

    const submit = (e) => {
        e.preventDefault();
        if (!form.code?.trim())    { Swal.fire({ icon: "warning", text: "Code obligatoire." });              return; }
        if (!form.nom?.trim())     { Swal.fire({ icon: "warning", text: "Nom obligatoire." });               return; }
        if (!form.type)            { Swal.fire({ icon: "warning", text: "Type obligatoire." });              return; }
        if (!form.domaineId)       { Swal.fire({ icon: "warning", text: "Veuillez choisir un domaine." });   return; }
        const payload = { code: form.code.trim(), nom: form.nom.trim(), description: form.description || "", type: form.type, domaineId: Number(form.domaineId), themeId: form.themeId ? Number(form.themeId) : null };
        if (editing?.id) dispatch(updateSkillRequest(editing.id, payload));
        else             dispatch(createSkillRequest(payload));
        closeModal();
    };

    const confirmDelete = (s) => {
        Swal.fire({ title: "Supprimer", text: `Supprimer le skill "${s.code}" ?`, icon: "warning", showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non", confirmButtonColor: "#d33" })
            .then((res) => { if (res.isConfirmed) dispatch(deleteSkillRequest(s.id)); });
    };

    const openBulk  = () => { setBulkOpen(true); setBulkFile(null); setBulkDryRun(true); setBulkAction("add"); dispatch(clearSkillsImportResult()); };
    const closeBulk = () => { setBulkOpen(false); setBulkFile(null); dispatch(clearSkillsImportResult()); };
    const runBulk   = () => {
        if (!bulkFile) { Swal.fire({ icon: "warning", text: "Veuillez sélectionner un fichier Excel." }); return; }
        dispatch(importSkillsExcelRequest(bulkFile, { dryRun: bulkDryRun, action: bulkAction }));
    };

    return (
        <>
            {/* ── En-tête ── */}
            <div className="crHeader">
                <div>
                    <h1>Gestion des compétences</h1>
                    <p>Gérez votre référentiel de compétences</p>
                </div>
                <div className="crHeaderActions">
                    <button className="crBtn crBtn--ghost" onClick={() => dispatch(downloadSkillsTemplateRequest())} disabled={bulkLoading} type="button">
                        <Download size={16} /> Télécharger template
                    </button>
                    <button className="crBtn crBtn--ghost" onClick={() => dispatch(exportSkillsRequest())} disabled={bulkLoading} type="button">
                        <FileDown size={16} /> Export
                    </button>
                    <button className="crBtn crBtn--primary" onClick={openBulk} type="button">
                        <Upload size={16} /> Ajout massif
                    </button>
                    <button className="crBtn crBtn--primary" onClick={openCreate} type="button">
                        <Plus size={16} /> Ajouter une compétence
                    </button>
                </div>
            </div>

            {/* ── Filtres ── */}
            <div className="crToolbar">
                <input type="text" className="crSearch" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code, nom, description, domaine..." />
                <select className="crToolbarSelect" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                    <option value="">Tous les types</option>
                    {SKILL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <div className="crMeta">{loading ? "..." : `${filtered.length} compétence${filtered.length !== 1 ? "s" : ""}`}</div>
            </div>

            {/* ── Tableau ── */}
            <div className="crWrap">
                <div className="crCard">
                    <div className="crBody">
                        <div className="crTopRow">
                            <h2 className="crTitle">Compétences ({loading ? "..." : filtered.length})</h2>
                        </div>
                        <div className="crTableWrap">
                            <table className="crTable">
                                <thead>
                                <tr>
                                    <th>Code</th><th>Nom</th><th>Type</th><th>Domaine</th><th>Thème</th><th>Description</th><th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.length === 0 ? (
                                    <tr><td colSpan={7} className="crEmptyCell">Aucune compétence</td></tr>
                                ) : (
                                    filtered.map((s) => (
                                        <tr key={s.id}>
                                            <td><span className="crStrong">{s.code}</span></td>
                                            <td>{s.nom || "—"}</td>
                                            <td><span className={`crBadge ${s.type === "METIER" ? "crBadge--blue" : "crBadge--green"}`}>{s.type}</span></td>
                                            <td className="crMuted">{s.domaineNom || "—"}</td>
                                            <td className="crMuted">{s.themeNom   || "—"}</td>
                                            <td className="crMuted">{s.description ? (s.description.length > 60 ? s.description.slice(0, 60) + "…" : s.description) : "—"}</td>
                                            <td className="is-right">
                                                <div className="crRowActions">
                                                    <button className="crIconBtn" title="Modifier" onClick={() => openEdit(s)} type="button"><Edit2 size={16} /></button>
                                                    <button className="crIconBtn crIconBtn--danger" title="Supprimer" onClick={() => confirmDelete(s)} type="button"><Trash2 size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ══ Modal CRUD ══ */}
            <Modal open={isModalOpen} title={editing ? "Modifier compétence" : "Ajouter compétence"} onClose={closeModal} size="lg"
                   footer={
                       <>
                           <button className="crBtn crBtn--ghost" onClick={closeModal} disabled={loading} type="button">Annuler</button>
                           <button className="crBtn crBtn--primary" onClick={submit} disabled={loading} type="button">
                               <Save size={16} />{loading ? "En cours..." : editing ? "Enregistrer" : "Créer"}
                           </button>
                       </>
                   }
            >
                <form className="crFormContent" onSubmit={submit}>
                    <div className="crFormSection">
                        <h3 className="crFormTitle">Identification</h3>
                        <div className="crFormGrid">
                            <div className="crField"><label>Code *</label><input name="code" value={form.code} onChange={onChange} /></div>
                            <div className="crField"><label>Nom *</label><input name="nom" value={form.nom} onChange={onChange} /></div>
                            <div className="crField">
                                <label>Type *</label>
                                <select name="type" value={form.type} onChange={onChange}>
                                    {SKILL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="crFormSection">
                        <h3 className="crFormTitle">Classification</h3>
                        <div className="crFormGrid">
                            <div className="crField">
                                <label>Domaine *</label>
                                <select name="domaineId" value={form.domaineId} onChange={onSelectDomaine}>
                                    <option value="">-- choisir --</option>
                                    {(domaines || []).map((d) => <option key={d.id} value={d.id}>{d.nom} ({d.reference})</option>)}
                                </select>
                            </div>
                            <div className="crField">
                                <label>Thème</label>
                                <select name="themeId" value={form.themeId} onChange={onChange}>
                                    <option value="">-- choisir --</option>
                                    {themesToUse.filter((t) => !form.domaineId || Number(t.domaineId) === Number(form.domaineId)).map((t) => <option key={t.id} value={t.id}>{t.nom} ({t.reference})</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="crFormSection">
                        <h3 className="crFormTitle">Détail</h3>
                        <div className="crFormGrid">
                            <div className="crField crField--full"><label>Description</label><textarea name="description" value={form.description} onChange={onChange} rows={4} /></div>
                        </div>
                    </div>
                    <button type="submit" style={{ display: "none" }} />
                </form>
            </Modal>

            {/* ══ Modal Ajout massif ══ */}
            <Modal open={bulkOpen} title="Ajout massif (Excel)" onClose={closeBulk} size="lg"
                   footer={
                       <>
                           <button className="crBtn crBtn--ghost" type="button" onClick={() => dispatch(downloadSkillsTemplateRequest())} disabled={bulkLoading}>
                               <FileSpreadsheet size={16} /> Télécharger template
                           </button>
                           <button className="crBtn crBtn--primary" type="button" onClick={runBulk} disabled={bulkLoading}>
                               <Upload size={16} />{bulkLoading ? "Traitement..." : bulkDryRun ? "Prévisualiser" : "Exécuter"}
                           </button>
                       </>
                   }
            >
                <div className="crFormContent">
                    <div className="crFormSection">
                        <h3 className="crFormTitle">Paramètres d'import</h3>
                        <div className="crFormGrid">
                            <div className="crField">
                                <label>Action</label>
                                <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
                                    <option value="add">ADD (nouveaux)</option>
                                    <option value="edit">EDIT (mise à jour via code)</option>
                                </select>
                            </div>
                            <div className="crField">
                                <label>Mode</label>
                                <label className="fmCheck">
                                    <input type="checkbox" checked={bulkDryRun} onChange={(e) => setBulkDryRun(e.target.checked)} />
                                    Dry-run (prévisualiser sans insertion)
                                </label>
                            </div>
                            <div className="crField crField--full">
                                <label>Fichier Excel</label>
                                <input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} />
                            </div>
                        </div>
                    </div>
                    {importResult && (
                        <div className="crFormSection">
                            <h3 className="crFormTitle">Résultat</h3>
                            <div className="fmImportSummary">
                                <span><b>Action :</b> {importResult.action}</span>
                                <span><b>Dry-run :</b> {String(importResult.dryRun)}</span>
                                <span><b>Total :</b> {importResult.totalRows}</span>
                                <span><b>OK :</b> {importResult.createdCount}</span>
                                <span><b>Ignorées :</b> {importResult.skippedCount}</span>
                                <span><b>Erreurs :</b> {(importResult.errorsSkill || []).length}</span>
                            </div>
                            {(importResult.errorsSkill || []).length > 0 && (
                                <div className="fmBlock">
                                    <div className="fmBlockTitle">Erreurs</div>
                                    <div className="crTableWrap">
                                        <table className="crTable">
                                            <thead><tr><th>Ligne</th><th>Colonne</th><th>Message</th></tr></thead>
                                            <tbody>
                                            {importResult.errorsSkill.map((er, idx) => (
                                                <tr key={idx}><td className="crMuted">{er.row}</td><td><span className="crStrong">{er.column}</span></td><td className="crMuted">{er.message}</td></tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {(importResult.previewSkill || []).length > 0 && (
                                <div className="fmBlock">
                                    <div className="fmBlockTitle">Prévisualisation (max 20)</div>
                                    <div className="crTableWrap">
                                        <table className="crTable">
                                            <thead><tr><th>Code</th><th>Nom</th><th>Type</th><th>Domaine</th><th>Thème</th></tr></thead>
                                            <tbody>
                                            {importResult.previewSkill.slice(0, 20).map((p, idx) => (
                                                <tr key={idx}>
                                                    <td><span className="crStrong">{p.code}</span></td>
                                                    <td className="crMuted">{p.nom}</td>
                                                    <td><span className={`crBadge ${p.type === "METIER" ? "crBadge--blue" : "crBadge--green"}`}>{p.type}</span></td>
                                                    <td className="crMuted">{p.domaineReference || p.domaineNom || "—"}</td>
                                                    <td className="crMuted">{p.themeReference  || p.themeNom  || "—"}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="fmHint">Affichage limité à 20 lignes.</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

/* ══════════════════════════════════════════════
   ONGLET — GESTION DES NIVEAUX
══════════════════════════════════════════════ */
const NiveauxTab = () => {
    const dispatch = useDispatch();

    const { niveauSkills, loading } = useSelector((state) => state.niveauSkill || {});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing,     setEditing]     = useState(null);
    const [form,        setForm]        = useState(emptyNiveauForm);
    const [q,           setQ]           = useState("");

    useEffect(() => { dispatch(fetchNiveauSkillsRequest()); }, [dispatch]);

    const niveaux  = useMemo(() => (Array.isArray(niveauSkills) ? [...niveauSkills].sort((a, b) => a.niveau - b.niveau) : []), [niveauSkills]);
    const filtered = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        return niveaux.filter((n) =>
            !t ||
            (n.nom         || "").toLowerCase().includes(t) ||
            (n.description || "").toLowerCase().includes(t) ||
            String(n.niveau).includes(t)
        );
    }, [niveaux, q]);

    const openCreate = () => { setEditing(null); setForm(emptyNiveauForm); setIsModalOpen(true); };
    const openEdit   = (n) => { setEditing(n); setForm({ nom: n.nom || "", description: n.description || "", niveau: n.niveau ?? "" }); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); setEditing(null); setForm(emptyNiveauForm); };

    const onChange = (e) => { const { name, value } = e.target; setForm((p) => ({ ...p, [name]: value })); };

    const submit = (e) => {
        e.preventDefault();
        if (!form.nom?.trim())            { Swal.fire({ icon: "warning", text: "Nom obligatoire." });            return; }
        if (form.niveau === "" || form.niveau === null) { Swal.fire({ icon: "warning", text: "Niveau obligatoire." }); return; }
        if (Number(form.niveau) < 1)      { Swal.fire({ icon: "warning", text: "Le niveau doit être ≥ 1." });   return; }
        const payload = { nom: form.nom.trim(), description: form.description || "", niveau: Number(form.niveau) };
        if (editing?.id) dispatch(updateNiveauSkillRequest(editing.id, payload));
        else             dispatch(createNiveauSkillRequest(payload));
        closeModal();
    };

    const confirmDelete = (n) => {
        Swal.fire({ title: "Supprimer", text: `Supprimer le niveau "${n.nom}" ?`, icon: "warning", showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non", confirmButtonColor: "#d33" })
            .then((res) => { if (res.isConfirmed) dispatch(deleteNiveauSkillRequest(n.id)); });
    };

    /* badge couleur selon niveau */
    const levelBadgeClass = (n) => {
        if (n <= 1) return "crBadge--grey";
        if (n <= 2) return "crBadge--green";
        if (n <= 3) return "crBadge--orange";
        return "crBadge--red";
    };

    return (
        <>
            {/* ── En-tête ── */}
            <div className="crHeader">
                <div>
                    <h1>Gestion des niveaux</h1>
                    <p>Définissez les niveaux de maîtrise des compétences</p>
                </div>
                <div className="crHeaderActions">
                    <button className="crBtn crBtn--primary" onClick={openCreate} type="button">
                        <Plus size={16} /> Ajouter un niveau
                    </button>
                </div>
            </div>

            {/* ── Filtres ── */}
            <div className="crToolbar">
                <input type="text" className="crSearch" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, description, numéro..." />
                <div className="crMeta">{loading ? "..." : `${filtered.length} niveau${filtered.length !== 1 ? "x" : ""}`}</div>
            </div>

            {/* ── Tableau ── */}
            <div className="crWrap">
                <div className="crCard">
                    <div className="crBody">
                        <div className="crTopRow">
                            <h2 className="crTitle">Niveaux ({loading ? "..." : filtered.length})</h2>
                        </div>
                        <div className="crTableWrap">
                            <table className="crTable">
                                <thead>
                                <tr>
                                    <th>Niveau</th>
                                    <th>Nom</th>
                                    <th>Description</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.length === 0 ? (
                                    <tr><td colSpan={4} className="crEmptyCell">Aucun niveau défini</td></tr>
                                ) : (
                                    filtered.map((n) => (
                                        <tr key={n.id}>
                                            <td>
                                                    <span className={`crBadge ${levelBadgeClass(n.niveau)}`}>
                                                        <Layers size={11} style={{ marginRight: 4 }} />
                                                        Niveau {n.niveau}
                                                    </span>
                                            </td>
                                            <td><span className="crStrong">{n.nom}</span></td>
                                            <td className="crMuted">
                                                {n.description
                                                    ? n.description.length > 80 ? n.description.slice(0, 80) + "…" : n.description
                                                    : "—"}
                                            </td>
                                            <td className="is-right">
                                                <div className="crRowActions">
                                                    <button className="crIconBtn" title="Modifier" onClick={() => openEdit(n)} type="button"><Edit2 size={16} /></button>
                                                    <button className="crIconBtn crIconBtn--danger" title="Supprimer" onClick={() => confirmDelete(n)} type="button"><Trash2 size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ══ Modal CRUD ══ */}
            <Modal open={isModalOpen} title={editing ? "Modifier niveau" : "Ajouter un niveau"} onClose={closeModal} size="lg"
                   footer={
                       <>
                           <button className="crBtn crBtn--ghost" onClick={closeModal} disabled={loading} type="button">Annuler</button>
                           <button className="crBtn crBtn--primary" onClick={submit} disabled={loading} type="button">
                               <Save size={16} />{loading ? "En cours..." : editing ? "Enregistrer" : "Créer"}
                           </button>
                       </>
                   }
            >
                <form className="crFormContent" onSubmit={submit}>
                    <div className="crFormSection">
                        <h3 className="crFormTitle">Identification</h3>
                        <div className="crFormGrid">
                            <div className="crField">
                                <label>Numéro de niveau *</label>
                                <input name="niveau" type="number" min="1" value={form.niveau} onChange={onChange} placeholder="ex: 1" />
                            </div>
                            <div className="crField">
                                <label>Nom *</label>
                                <input name="nom" value={form.nom} onChange={onChange} placeholder="ex: Débutant" />
                            </div>
                            <div className="crField crField--full">
                                <label>Description</label>
                                <textarea name="description" value={form.description} onChange={onChange} rows={4} placeholder="Décrivez ce niveau de maîtrise..." />
                            </div>
                        </div>
                    </div>
                    <button type="submit" style={{ display: "none" }} />
                </form>
            </Modal>
        </>
    );
};

/* ══════════════════════════════════════════════
   COMPOSANT PRINCIPAL avec onglets
══════════════════════════════════════════════ */
const SkillManagement = () => {
    const [activeTab, setActiveTab] = useState("skills");

    return (
        <div className="crPage">
            {/* ── Onglets ── */}
            <div className="crTabBar">
                <button
                    className={`crTab ${activeTab === "skills" ? "crTab--active" : ""}`}
                    onClick={() => setActiveTab("skills")}
                    type="button"
                >
                    Gestion des compétences
                </button>
                <button
                    className={`crTab ${activeTab === "niveaux" ? "crTab--active" : ""}`}
                    onClick={() => setActiveTab("niveaux")}
                    type="button"
                >
                    <Layers size={14}/>
                    Gestion des niveaux
                </button>
                <button
                    className={`crTab ${activeTab === "modes" ? "crTab--active" : ""}`}
                    onClick={() => setActiveTab("modes")}
                    type="button"
                >
                    <ClipboardList size={14}/>
                    Modes d'évaluation
                </button>
            </div>

            {activeTab === "skills" && <SkillsTab/>}
            {activeTab === "niveaux" && <NiveauxTab/>}
            {activeTab === "modes" && <ModesEvaluationTab />}

        </div>
    );
};

export default SkillManagement;